import React, { forwardRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { AutoComplete, Col, Divider, Form, Row, Select } from 'antd';
import { LabeledValue } from 'antd/es/select';
import { IdAndNameType } from '../api/types';
import { useStoreActions, useStoreState } from '../store/hooks';
import { debounce } from '../utils/DataManagement';
import { GENERAL_PAGE_SIZE } from '../store/general';

export interface Props {
  value?: string;
  onChange?: Function;
  disabled?: boolean;
  customLabelCol?: number;
  isFilter?: boolean;
}

const SupplierInput = forwardRef<HTMLDivElement, Props>(
  ({ value, onChange, disabled, customLabelCol, isFilter = false }, ref) => {
    const {
      setSearchSupplier,
      fetchSuppliers,
      fetchSupplier,
      setSupplier,
    } = useStoreActions(actions => actions.general);

    const loadingSuppliers = useStoreState(state => state.general.loadingSuppliers);
    const supplier = useStoreState(state => state.general.supplier);
    const { Option, OptGroup } = AutoComplete;
    const [dataSource, setDataSource] = useState<IdAndNameType[]>([]);
    const [total, setTotal] = useState(-1);
    const supplierFormItemLayout = {
      labelCol: { span: customLabelCol || 6 },
      wrapperCol: { span: 18 },
    };

    const handleChange = (index: number, val: LabeledValue) => {
      if (onChange) {
        let valueClone = '';
        if (val) {
          // @ts-ignore
          valueClone = val?.label?.props?.children[0].props.children;
        }

        fetchSupplier(valueClone).then((data: IdAndNameType | boolean) => {
          if (data) {
            setSupplier(data as IdAndNameType);
          }
          onChange(valueClone);
        });
      }
    };

    const getSupplierInValue = (externalId?: string): IdAndNameType => {
      if (!externalId || !supplier) {
        return { id: '', name: '', externalId: '' };
      }

      return { ...supplier, externalId: supplier.id };
    };

    const onSearch = async (searchText: string) => {
      setSearchSupplier(searchText);
      //
      // if (!searchText || searchText.length < 3) {
      //   setDataSource([]);
      //   setTotal(-1);
      //   return;
      // }

      fetchSuppliers().then((data: any) => {
        if (data) {
          setDataSource(data.data);
          setTotal(data.total);
        } else {
          setDataSource([]);
          setTotal(-1);
        }
      });
    };

    const onSearchDebounce = debounce(onSearch, 700);

    const onFocus = () => {
      onSearchDebounce('').then();
    };

    const formatOptionText = (oneValue: IdAndNameType): any => {
      return (
        <>
          <span
            className='text-ellipsis'
            style={{
              float: 'left',
              maxWidth: '130px',
              display: 'inline-block',
            }}
          >
            {oneValue.id}
          </span>
          <span
            className='text-ellipsis'
            style={{
              float: 'right',
              width: '130px',
              maxWidth: '130px',
              textAlign: 'right',
            }}
          >
            {oneValue.name}
          </span>
        </>
      );
    };

    const renderGroupTitle = () => {
      return (
        <div>
          <FormattedMessage id='max_discounts.detail.supplier_id' />
          <span style={{ float: 'right' }}>
            {' '}
            <FormattedMessage id='pricing_rules.detail.supplier_name' />
          </span>
        </div>
      );
    };

    const options = [
      <OptGroup key='GROUPSUP' label={renderGroupTitle()}>
        {dataSource.map(data => (
          <Option key={`${data.id}`} value={`${data.id} - ${data.name}`}>
            {formatOptionText(data)}
          </Option>
        ))}
      </OptGroup>,
    ];

    return (
      <div ref={ref}>
        <Row
          type='flex'
          align='middle'
          justify='space-around'
          className='minimal-size'
        >
          <Col span={24}>
            <Form.Item
              label={<FormattedMessage id='max_discounts.detail.supplier' />}
              required={!isFilter}
              // help={<FormattedMessage id='common.combo_search_placeholder' />}
              {...supplierFormItemLayout}
            >
              <Select
                showSearch
                allowClear={isFilter && !!value}
                disabled={disabled}
                labelInValue
                filterOption={false}
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={false}
                onChange={(val: LabeledValue) => handleChange(0, val)}
                dropdownStyle={{ width: 300 }}
                onSearch={onSearchDebounce}
                value={
                  {
                    key: value || '',
                    label:
                      getSupplierInValue(value) &&
                      (getSupplierInValue(value).externalId ||
                        getSupplierInValue(value).name)
                        ? `${getSupplierInValue(value).externalId} - ${
                          getSupplierInValue(value).name
                        }`
                        : undefined,
                  } as LabeledValue
                }
                onFocus={() => {
                  onFocus();
                }}
                notFoundContent={null}
                loading={loadingSuppliers}
                dropdownRender={menu => (
                  <div>
                    {menu}
                    {total > GENERAL_PAGE_SIZE && (
                      <>
                        <Divider style={{ margin: '4px 0' }} />
                        <div
                          style={{ padding: '5px 10px' }}
                          className='ant-form-explain'
                        >
                          <FormattedMessage
                            id='common.combo_search_more_results'
                            values={{ total, limit: GENERAL_PAGE_SIZE }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              >
                {options}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </div>
    );
  },
);

export default SupplierInput;
